import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CustomerInfoCard from './components/CustomerInfoCard';
import NextStepsSection from './components/NextStepsSection';
import ContactInfoSection from './components/ContactInfoSection';

import { dealershipService } from '../../services/dealershipService';

const ApplicationSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [applicationData, setApplicationData] = useState(null);
  const [dealershipSlug, setDealershipSlug] = useState(null);
  const [dealership, setDealership] = useState(null);

  useEffect(() => {
    // Get application data and dealership slug from navigation state
    const data = location?.state?.applicationData;
    const slug = location?.state?.dealershipSlug;
    
    if (!data) {
      // Redirect to home if no data available
      navigate('/', { replace: true });
      return;
    }
    
    setApplicationData(data);
    setDealershipSlug(slug); // Store slug for redirect back to partner page

    // Fetch dealership data if slug is available
    const fetchDealership = async () => {
      if (slug) {
        try {
          const dealershipData = await dealershipService?.getBySlug(slug);
          setDealership(dealershipData);
        } catch (err) {
          console.error('Error loading dealership:', err);
        }
      }
    };

    fetchDealership();
  }, [location, navigate]);

  if (!applicationData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Lade Antragsdaten...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Antrag erfolgreich eingereicht | Grid Balance</title>
        <meta name="description" content="Ihr THG-Prämienantrag wurde erfolgreich eingereicht. Hier finden Sie alle wichtigen Informationen zu Ihrem Antrag." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/30 to-background">
        {/* Success Header */}
        <div className="bg-gradient-to-r from-success via-success/90 to-success/80 text-white py-12 md:py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl animate-bounce">
              <Icon name="CheckCircle2" size={56} color="var(--color-success)" />
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-4">
              Antrag erfolgreich eingereicht!
            </h1>
            <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto">
              Vielen Dank für Ihre Anmeldung zur THG-Prämie. Wir haben Ihren Antrag erhalten und werden ihn schnellstmöglich bearbeiten.
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
          {/* Customer Information Card */}
          <CustomerInfoCard applicationData={applicationData} />

          {/* Email Confirmation Notice */}
          <div className="bg-primary/10 border-2 border-primary/30 rounded-xl p-6 mb-8 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                  <Icon name="Mail" size={24} color="var(--color-primary)" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="font-heading font-semibold text-foreground text-lg mb-2">
                  Bestätigungs-E-Mail versendet
                </h3>
                <p className="text-muted-foreground text-sm md:text-base">
                  Wir haben Ihnen eine Bestätigungs-E-Mail an <strong className="text-foreground">{applicationData?.customer_email}</strong> gesendet. 
                  Bitte überprüfen Sie auch Ihren Spam-Ordner, falls Sie die E-Mail nicht finden.
                </p>
              </div>
            </div>
          </div>

          {/* Next Steps Section */}
          <NextStepsSection dealership={dealership} />

          {/* Contact Information */}
          <ContactInfoSection />

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Button
              variant="default"
              size="lg"
              onClick={() => {
                // Smart redirect: go back to partner landing page if available, otherwise homepage
                const redirectPath = dealershipSlug ? `/${dealershipSlug}` : '/';
                navigate(redirectPath);
              }}
              iconName="Home"
              iconPosition="left"
              className="sm:min-w-[200px]"
            >
              {dealershipSlug ? 'Zurück zur Partner-Seite' : 'Zur Startseite'}
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => window.print()}
              iconName="Printer"
              iconPosition="left"
              className="sm:min-w-[200px]"
            >
              Seite drucken
            </Button>
          </div>
        </div>

        {/* Footer Note */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <div className="bg-muted/50 rounded-lg p-6 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Icon name="ShieldCheck" size={20} color="var(--color-success)" />
              <p className="font-semibold text-foreground">Ihre Daten sind sicher</p>
            </div>
            <p className="text-sm text-muted-foreground">
              Alle Ihre Daten werden verschlüsselt übertragen und gemäß DSGVO verarbeitet. 
              Wir verwenden Ihre Informationen ausschließlich zur Bearbeitung Ihres THG-Antrags.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default ApplicationSuccess;